import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { SnackbarProvider as NotistackSnackbarProvider, SnackbarKey } from 'notistack';
import IconButton from '@material-ui/core/IconButton';
import Slide, { SlideProps } from '@material-ui/core/Slide';

import './SnackbarProvider.css';
import MaterialIcon from 'components/icon/MaterialIcon';

//
// --- Types ---

export interface SnackbarProviderProps {}

//
// --- Constants ---

const ICON_CLASS = 'supplier-search-snackbar-variant-icon';

//
// --- Styles ---

/**
 * NOTE: Cannot use vanilla CSS for variants. Works fine in dev mode, but class
 * names are changed in build version and no longer available. Any selectors for
 * "Snackbar-*" class names will not work. All other styles there is a work-around.
 */

const useStyles = makeStyles(theme => ({
  success: { borderColor: `${theme.palette.success.main} !important` },
  error: { borderColor: `${theme.palette.error.main} !important` },
  warning: { borderColor: `${theme.palette.warning.main} !important` },
  info: { borderColor: `${theme.palette.info.main} !important` }
}));

//
// --- Helpers ---

const TransitionUp: React.FC<SlideProps> = props => <Slide {...props} direction="up" />;

//
// --- SnackbarProvider Component ---

const SnackbarProvider: React.FC<SnackbarProviderProps> = props => {
  const { children } = props;

  const notistackRef = React.useRef<NotistackSnackbarProvider>(null);
  const classes = useStyles(props);

  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef?.current?.closeSnackbar(key);
  };

  return (
    <NotistackSnackbarProvider
      action={key => (
        <IconButton onClick={onClickDismiss(key)}>
          <MaterialIcon className="supplier-search-snackbar-close-icon" name="close" />
        </IconButton>
      )}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      autoHideDuration={3000}
      classes={{
        variantSuccess: classes.success,
        variantError: classes.error,
        variantWarning: classes.warning,
        variantInfo: classes.info
      }}
      className="supplier-search-snackbar"
      domRoot={document.getElementById('supplier-search-notification-container') || undefined}
      iconVariant={{
        error: <MaterialIcon className={`${ICON_CLASS} ${ICON_CLASS}-error`} name="error" />,
        info: <MaterialIcon className={`${ICON_CLASS} ${ICON_CLASS}-info`} name="info" />,
        success: <MaterialIcon className={`${ICON_CLASS} ${ICON_CLASS}-success`} name="check_circle" />,
        warning: <MaterialIcon className={`${ICON_CLASS} ${ICON_CLASS}-warning`} name="warning" />
      }}
      maxSnack={2}
      ref={notistackRef}
      TransitionComponent={TransitionUp}
      dense
    >
      {children}
    </NotistackSnackbarProvider>
  );
};

export default SnackbarProvider;
